import { ref, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { defineStore, storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppRoute } from '@/stores/app/route';
import { useEditor } from '@/stores/editor/editor';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useCommenting } from '@/stores/commenting/commenting';
import { useTracking } from '@/stores/tracking/tracking';

const JOB_PROFILE_EDITOR_ROUTES = ['job-profile-view', 'job-profile-hris-view'];

export const useDocumentRightPanel = defineStore('documentRightPanel', () => {
  const router = useRouter();
  const { route } = storeToRefs(useAppRoute());
  const { jobId } = storeToRefs(useEditor());
  const { jobProfile, hasPendingApprovalFlows } = storeToRefs(useCurrentJobProfile());
  const currentTabRef = ref('');

  const { unresolvedThreadCount } = storeToRefs(useCommenting());

  const { checkRbacPermissions } = useAppAuthData();

  const currentTab = computed(() => {
    if (currentTabRef.value) {
      return currentTabRef.value;
    }

    if (hasPendingApprovalFlows.value) {
      return 'approvals';
    }

    if (jobProfile.value?.type_key === 'job_profile_template'
      && checkRbacPermissions(['listDraftProfilesByProfileTemplate'])
    ) {
      return 'template-usage';
    }

    if (unresolvedThreadCount.value > 0) {
      return 'comments';
    }

    return 'history';
  });

  watch([jobId, jobProfile], ([newJobId, newJobProfile], [oldJobId, oldJobProfile]) => {
    if (newJobId !== oldJobId || newJobProfile?.ulid !== oldJobProfile?.ulid) {
      currentTabRef.value = '';
      useCommenting().setThreadCounts(null);
    }
  });

  const setCurrentTab = (tab, { track = true } = {}) => {
    currentTabRef.value = tab;

    const routeName = route.value?.name;

    if (!JOB_PROFILE_EDITOR_ROUTES.includes(routeName)) {
      return;
    }

    if (track) {
      useTracking().jpEditorOpenedTab({ tab });
    }

    const currentQuery = route.value?.query || {};

    // Don't update the query if the tab is already the current tab
    if (currentQuery.sidebar === tab) {
      return;
    }

    router.replace({ query: { ...currentQuery, sidebar: tab } }).catch(() => {});
  };

  return {
    currentTab,
    setCurrentTab,
  };
});
